import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, CSSProperties } from "react";
import type { ClassId } from "../game/types";
import { CINEMATICS, cinematicDuration, resolveCinematicText, type CinematicId, type CinematicShot } from "../cinematics/content";
import { CINEMATIC_MEDIA } from "../cinematics/media";
import "./CinematicPlayer.css";

interface Props {
  cinematicId: CinematicId;
  classId?: ClassId;
  reducedMotion?: boolean;
  soundEnabled?: boolean;
  onComplete: (skipped: boolean) => void;
}

const clamp = (value: number, minimum: number, maximum: number) => Math.min(maximum, Math.max(minimum, value));
const formatTime = (milliseconds: number) => {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

function locateShot(shots: CinematicShot[], elapsedMs: number) {
  let startMs = 0;
  for (let index = 0; index < shots.length; index += 1) {
    const shot = shots[index];
    if (elapsedMs < startMs + shot.durationMs || index === shots.length - 1) {
      return { shot, index, progress: clamp((elapsedMs - startMs) / shot.durationMs, 0, 1) };
    }
    startMs += shot.durationMs;
  }
  return { shot: shots[0], index: 0, progress: 0 };
}

export function CinematicVideoPlayer({ cinematicId, classId, reducedMotion = false, soundEnabled = true, onComplete }: Props) {
  const cinematic = CINEMATICS[cinematicId];
  const media = CINEMATIC_MEDIA[cinematicId];
  const totalDuration = cinematicDuration(cinematic);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const completed = useRef(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [needsPlay, setNeedsPlay] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const located = locateShot(cinematic.shots, elapsedMs);
  const shot = located.shot;

  const finish = (skipped: boolean) => {
    if (completed.current) return;
    completed.current = true;
    onComplete(skipped);
  };

  const play = async () => {
    try {
      await videoRef.current?.play();
      setNeedsPlay(false);
      setPaused(false);
    } catch {
      setNeedsPlay(true);
    }
  };

  useEffect(() => {
    completed.current = false;
    setElapsedMs(0);
    setPaused(false);
    setMuted(true);
    setNeedsPlay(false);
    setVideoFailed(false);
    void play();
  }, [cinematicId]);

  useEffect(() => {
    if (!videoFailed || paused) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const delta = Math.min(100, now - last);
      last = now;
      let reachedEnd = false;
      setElapsedMs((current) => {
        const next = Math.min(totalDuration, current + delta);
        reachedEnd = next >= totalDuration;
        return next;
      });
      if (reachedEnd) finish(false);
      else frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [paused, totalDuration, videoFailed]);

  const seek = (value: number) => {
    const next = clamp(value, 0, totalDuration);
    setElapsedMs(next);
    if (videoRef.current && !videoFailed) videoRef.current.currentTime = next / 1000;
  };

  const toggleAudio = async () => {
    const video = videoRef.current;
    if (!video || !soundEnabled) return;
    const nextMuted = !muted;
    video.muted = nextMuted;
    setMuted(nextMuted);
    if (!nextMuted && video.paused) await play();
  };

  const togglePause = () => {
    const video = videoRef.current;
    if (!video || videoFailed) {
      setPaused((value) => !value);
    } else if (video.paused) {
      void play();
    } else {
      video.pause();
      setPaused(true);
    }
  };

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement | null)?.matches("input, textarea, select")) return;
      if (event.key === "Escape") { event.preventDefault(); finish(true); }
      else if (event.key === " ") { event.preventDefault(); togglePause(); }
      else if (event.key === "ArrowRight") { event.preventDefault(); seek(elapsedMs + 10_000); }
      else if (event.key === "ArrowLeft") { event.preventDefault(); seek(elapsedMs - 10_000); }
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [elapsedMs, totalDuration, videoFailed]);

  const style = {
    "--cinematic-primary": shot.palette[0],
    "--cinematic-accent": shot.palette[1],
    "--cinematic-dark": shot.palette[2],
    "--shot-progress": located.progress,
  } as CSSProperties;

  return <section className={`cinematic-player cinematic-video-player ${reducedMotion ? "cinematic-reduced-motion" : ""}`} style={style} role="dialog" aria-modal="true" aria-label={`${cinematic.title} cinematic`}>
    {!videoFailed && <video ref={videoRef} className="cinematic-video" poster={media.poster} autoPlay muted playsInline preload="auto"
      onTimeUpdate={(event) => setElapsedMs(event.currentTarget.currentTime * 1000)}
      onPlay={() => { setPaused(false); setNeedsPlay(false); }} onPause={() => setPaused(true)} onEnded={() => finish(false)} onError={() => setVideoFailed(true)}>
      <source src={media.webm} type="video/webm" />
      <source src={media.mp4} type="video/mp4" />
    </video>}
    {videoFailed && <div className="cinematic-video-fallback" style={{ backgroundImage: `url(${media.poster})` }} aria-hidden="true" />}
    <div className="cinematic-film-grain" aria-hidden="true" />
    <div className="cinematic-letterbox top" /><div className="cinematic-letterbox bottom" />
    <header className="cinematic-header">
      <div><span>{cinematic.subtitle}</span><strong>{cinematic.title}</strong></div>
      <button className="cinematic-skip" onClick={() => finish(true)}><span>EXIT</span>Skip cinematic <kbd>Esc</kbd></button>
    </header>
    {needsPlay && <button className="cinematic-play-gate" onClick={() => void play()}><span>▶</span>Play cinematic</button>}
    <div className="cinematic-shot-number"><span>{shot.chapter}</span><b>{String(located.index + 1).padStart(2, "0")}</b></div>
    <article className="cinematic-caption" aria-live="polite" aria-atomic="true" key={`${cinematicId}-${located.index}-caption`}>
      <span>{shot.chapter}</span><h1>{resolveCinematicText(shot.title, classId)}</h1><p>{resolveCinematicText(shot.body, classId)}</p>
      {shot.dialogue && <blockquote><b>{shot.speaker ? resolveCinematicText(shot.speaker, classId) : ""}</b><q>{resolveCinematicText(shot.dialogue, classId)}</q></blockquote>}
    </article>
    <footer className="cinematic-controls">
      <div><button onClick={togglePause}>{paused ? "▶ Play" : "Ⅱ Pause"}</button>{soundEnabled && <button onClick={() => void toggleAudio()}>{muted ? "◌ Enable audio" : "◉ Audio on"}</button>}<span>{formatTime(elapsedMs)} / {formatTime(totalDuration)}</span><small>Space pause · ← → seek 10s</small></div>
      <input type="range" min={0} max={totalDuration} step={100} value={Math.min(elapsedMs, totalDuration)} onChange={(event: ChangeEvent<HTMLInputElement>) => seek(Number(event.target.value))} aria-label="Cinematic timeline" />
      <div className="cinematic-shot-dots" aria-hidden="true">{cinematic.shots.map((entry, index) => <i key={`${entry.title}-${index}`} className={index < located.index ? "complete" : index === located.index ? "active" : ""} style={{ flex: entry.durationMs }} />)}</div>
    </footer>
    <div className="cinematic-edge" style={{ width: `${elapsedMs / totalDuration * 100}%` }} />
  </section>;
}
